import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  PaperAirplaneIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  BuildingOfficeIcon,
  UserIcon,
  CheckIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import FreeHTMLEditor from '../../components/ui/FreeHTMLEditor';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { Lawsuit as SharedLawsuit, Document } from '@tvia-ai/shared';

interface DocumentSelection {
  toAccused: string[];
  toCourt: string[];
}

const StartSubmissionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentSelection>({
    toAccused: [],
    toCourt: []
  });
  const [step, setStep] = useState<'selection' | 'confirmation' | 'submission'>('selection');
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);

  const handleDocumentSaved = (_updatedDocument: any) => {
    // Close the editor and refresh the lawsuit data to show new documents
    setEditingDocument(null);
    // Invalidate and refetch the lawsuit data to get updated documents
    queryClient.invalidateQueries({ queryKey: ['lawsuit', id] });
  };

  // Helper function to group documents by base name for version display
  const groupDocumentsByBaseName = (documents: Document[]) => {
    const groupedDocs = documents.reduce((groups: Record<string, Document[]>, doc) => {
      // Use originalName as the grouping key since it's now clean without suffixes
      const baseName = doc.originalName || doc.filename;
      
      if (!groups[baseName]) groups[baseName] = [];
      groups[baseName].push(doc);
      return groups;
    }, {});

    // Sort documents within each group by version (highest first)
    Object.keys(groupedDocs).forEach(baseName => {
      groupedDocs[baseName].sort((a, b) => (b.version || 1) - (a.version || 1));
    });

    return groupedDocs;
  };

  // Fetch lawsuit details
  const { data: lawsuit, isLoading, error } = useQuery<SharedLawsuit | undefined>({
    queryKey: ['lawsuit', id],
    queryFn: async () => {
      if (!id) return undefined;
      const response = await axios.get(`/api/lawsuits/${id}`);
      return response.data.data.lawsuit as SharedLawsuit;
    },
    enabled: !!id,
  });

  // Submit lawsuit mutation
  const submitLawsuitMutation = useMutation({
    mutationFn: async (data: { documentsToAccused: string[], documentsToCourt: string[] }) => {
      const response = await axios.post(`/api/lawsuits/${id}/submit`, data);
      return response.data;
    },
    onSuccess: () => {
      navigate(`/lawsuit/${id}?submitted=true`);
    },
  });

  const handleDocumentToggle = (documentId: string, destination: 'toAccused' | 'toCourt') => {
    setSelectedDocuments(prev => ({
      ...prev,
      [destination]: prev[destination].includes(documentId)
        ? prev[destination].filter(id => id !== documentId)
        : [...prev[destination], documentId]
    }));
  };

  const handleSubmit = async () => {
    setStep('submission');
    await submitLawsuitMutation.mutateAsync({
      documentsToAccused: selectedDocuments.toAccused,
      documentsToCourt: selectedDocuments.toCourt
    });
  };

  const getDocumentIcon = (doc: Document | { mimeType?: string; [key: string]: any }, colorClass?: string) => {
    const defaultColor = colorClass || 'text-gray-500';
    const blueColor = colorClass || 'text-blue-500';
    
    if ('isGenerated' in doc && doc.isGenerated) {
      return <ScaleIcon className={`h-5 w-5 ${blueColor}`} />;
    }
    return <DocumentTextIcon className={`h-5 w-5 ${defaultColor}`} />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !lawsuit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t('errors.lawsuitNotFound', 'Lawsuit not found')}
          </h3>
        </div>
      </div>
    );
  }

  const documents = lawsuit.documents || []; // Generated documents only
  const generatedDocuments = documents; // All documents are generated now
  const uploadedFiles = lawsuit.files || []; // User-uploaded files

  return (
    <>
      <Helmet>
        <title>{`${t('lawsuit.startSubmission', 'Start Submission')} - ${lawsuit.title} - tvia.ai`}</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white shadow rounded-lg mb-6 sm:mb-8">
            <div className="px-4 sm:px-6 py-6 sm:py-8">
              <div className="flex items-center mb-4">
                <PaperAirplaneIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {t('lawsuit.startSubmission', 'Start Submission')}
                </h1>
              </div>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                {t('lawsuit.submissionDescription', 'Choose which documents to send to the accused party and which to file with the courthouse to begin your lawsuit submission.')}
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-1">
                  {lawsuit.title}
                </h3>
                <p className="text-sm text-blue-800">
                  {t('lawsuit.accusedParty', 'Accused Party')}: {lawsuit.aiAnalysis?.accusedParty?.name || t('common.notSpecified', 'Not specified')}
                </p>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="bg-white shadow rounded-lg mb-6 sm:mb-8">
            <div className="px-4 sm:px-6 py-4">
              <div className="flex flex-wrap items-center justify-center sm:justify-between gap-3 sm:gap-4">
                <div className={`flex items-center ${step === 'selection' ? 'text-cyan-600' : 'text-green-600'}`}>
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                    step === 'selection' ? 'bg-cyan-100' : 'bg-green-100'
                  }`}>
                    {step === 'selection' ? '1' : <CheckIcon className="h-4 w-4" />}
                  </div>
                  <span className="ml-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                    {t('lawsuit.selectDocuments', 'Select Documents')}
                  </span>
                </div>
                <div className={`flex items-center ${
                  step === 'confirmation' ? 'text-cyan-600' :
                  step === 'submission' ? 'text-green-600' : 'text-gray-400'
                }`}>
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                    step === 'confirmation' ? 'bg-cyan-100' :
                    step === 'submission' ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {step === 'submission' ? <CheckIcon className="h-4 w-4" /> : '2'}
                  </div>
                  <span className="ml-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                    {t('lawsuit.confirmation', 'Confirmation')}
                  </span>
                </div>
                <div className={`flex items-center ${step === 'submission' ? 'text-cyan-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
                    step === 'submission' ? 'bg-cyan-100' : 'bg-gray-100'
                  }`}>
                    3
                  </div>
                  <span className="ml-2 text-xs sm:text-sm font-medium whitespace-nowrap">
                    {t('lawsuit.submission', 'Submission')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {step === 'selection' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* To Accused Party */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 sm:px-6 py-4 sm:py-6">
                  <div className="flex items-center mb-4">
                    <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mr-2 sm:mr-3 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                      {t('lawsuit.toAccusedParty', 'To Accused Party')}
                    </h2>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-red-800">
                          {t('lawsuit.accusedPartyInfo', 'These documents will be sent to notify the accused party of the lawsuit and provide them with the necessary information about your claims.')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      {t('lawsuit.recommendedDocuments', 'Recommended Documents')}
                    </h3>
                    {Object.entries(groupDocumentsByBaseName(generatedDocuments)).map(([baseName, docs]) => (
                      <div key={baseName} className="border border-gray-200 rounded-lg overflow-hidden">
                        {docs.map((doc, index) => {
                          const isLatest = index === 0;
                          const versionNumber = docs.length - index;
                          const isEdited = doc.originalName.includes('_edited') || doc.description?.includes('Edited version');
                          
                          return (
                            <div 
                              key={doc.id} 
                              className={`flex items-center p-3 ${index > 0 ? 'border-t border-gray-100' : ''} ${isLatest ? 'bg-green-50' : 'bg-gray-50'} hover:bg-opacity-75 transition-colors`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedDocuments.toAccused.includes(doc.id)}
                                onChange={() => handleDocumentToggle(doc.id, 'toAccused')}
                                className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                              />
                              <div className="ml-3 flex items-center flex-1">
                                <div className="flex flex-col items-center mr-3">
                                  {getDocumentIcon(doc, isLatest ? 'text-green-600' : 'text-gray-400')}
                                  {docs.length > 1 && (
                                    <span className={`text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${isLatest ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                                      v{versionNumber}
                                    </span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center">
                                    <p className={`text-sm font-medium truncate ${isLatest ? 'text-green-900' : 'text-gray-700'}`}>
                                      {doc.originalName}
                                    </p>
                                    {isLatest && (
                                      <span className="ml-2 px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">
                                        {t('common.latest', 'Latest')}
                                      </span>
                                    )}
                                    {isEdited && (
                                      <span className="ml-2 px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-medium">
                                        {t('common.edited', 'Edited')}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center text-xs text-gray-500 mt-1">
                                    <span>{t('lawsuit.generated', 'Generated')} • {(doc.size / 1024).toFixed(1)} KB</span>
                                    <span className="mx-2">•</span>
                                    <span>{new Date(doc.uploadedAt).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => setEditingDocument(doc)}
                                className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 min-h-[44px]"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('common.edit', 'Edit')}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    
                    <h3 className="text-sm font-medium text-gray-900 mb-3 mt-6">
                      {t('lawsuit.supportingEvidence', 'Supporting Evidence')}
                    </h3>
                    {uploadedFiles.map((doc) => (
                      <label key={doc.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.toAccused.includes(doc.id)}
                          onChange={() => handleDocumentToggle(doc.id, 'toAccused')}
                          className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                        />
                        <div className="ml-3 flex items-center flex-1">
                          {getDocumentIcon(doc)}
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{doc.originalName}</p>
                            <p className="text-xs text-gray-500">
                              {t('lawsuit.uploaded', 'Uploaded')} • {(doc.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* To Courthouse */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 sm:px-6 py-4 sm:py-6">
                  <div className="flex items-center mb-4">
                    <BuildingOfficeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500 mr-2 sm:mr-3 flex-shrink-0" />
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                      {t('lawsuit.toCourthouse', 'To Courthouse')}
                    </h2>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-blue-800">
                          {t('lawsuit.courthouseInfo', 'These documents will be filed with the court to officially begin your lawsuit proceedings.')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      {t('lawsuit.legalDocuments', 'Legal Documents')}
                    </h3>
                    {Object.entries(groupDocumentsByBaseName(generatedDocuments)).map(([baseName, docs]) => (
                      <div key={baseName} className="border border-gray-200 rounded-lg overflow-hidden">
                        {docs.map((doc, index) => {
                          const isLatest = index === 0;
                          const versionNumber = docs.length - index;
                          const isEdited = doc.originalName.includes('_edited') || doc.description?.includes('Edited version');
                          
                          return (
                            <div 
                              key={doc.id} 
                              className={`flex items-center p-3 ${index > 0 ? 'border-t border-gray-100' : ''} ${isLatest ? 'bg-green-50' : 'bg-gray-50'} hover:bg-opacity-75 transition-colors`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedDocuments.toCourt.includes(doc.id)}
                                onChange={() => handleDocumentToggle(doc.id, 'toCourt')}
                                className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                              />
                              <div className="ml-3 flex items-center flex-1">
                                <div className="flex flex-col items-center mr-3">
                                  {getDocumentIcon(doc, isLatest ? 'text-green-600' : 'text-gray-400')}
                                  {docs.length > 1 && (
                                    <span className={`text-xs font-medium mt-1 px-1.5 py-0.5 rounded ${isLatest ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                                      v{versionNumber}
                                    </span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center">
                                    <p className={`text-sm font-medium truncate ${isLatest ? 'text-green-900' : 'text-gray-700'}`}>
                                      {doc.originalName}
                                    </p>
                                    {isLatest && (
                                      <span className="ml-2 px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">
                                        {t('common.latest', 'Latest')}
                                      </span>
                                    )}
                                    {isEdited && (
                                      <span className="ml-2 px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-medium">
                                        {t('common.edited', 'Edited')}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center text-xs text-gray-500 mt-1">
                                    <span>{t('lawsuit.generated', 'Generated')} • {(doc.size / 1024).toFixed(1)} KB</span>
                                    <span className="mx-2">•</span>
                                    <span>{new Date(doc.uploadedAt).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => setEditingDocument(doc)}
                                className="ml-2 inline-flex items-center px-3 py-2 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 min-h-[44px]"
                              >
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('common.edit', 'Edit')}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    
                    <h3 className="text-sm font-medium text-gray-900 mb-3 mt-6">
                      {t('lawsuit.supportingEvidence', 'Supporting Evidence')}
                    </h3>
                    {uploadedFiles.map((doc) => (
                      <label key={doc.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.toCourt.includes(doc.id)}
                          onChange={() => handleDocumentToggle(doc.id, 'toCourt')}
                          className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                        />
                        <div className="ml-3 flex items-center flex-1">
                          {getDocumentIcon(doc)}
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{doc.originalName}</p>
                            <p className="text-xs text-gray-500">
                              {t('lawsuit.uploaded', 'Uploaded')} • {(doc.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'confirmation' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 sm:px-6 py-6 sm:py-8">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
                  {t('lawsuit.reviewSubmission', 'Review Your Submission')}
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('lawsuit.toAccusedParty', 'To Accused Party')} ({selectedDocuments.toAccused.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedDocuments.toAccused.map(docId => {
                        const doc = documents.find(d => d.id === docId);
                        return doc ? (
                          <div key={doc.id} className="flex items-center p-2 bg-red-50 rounded-lg">
                            {getDocumentIcon(doc)}
                            <span className="ml-2 text-sm text-gray-900">{doc.originalName}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('lawsuit.toCourthouse', 'To Courthouse')} ({selectedDocuments.toCourt.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedDocuments.toCourt.map(docId => {
                        const doc = documents.find(d => d.id === docId);
                        return doc ? (
                          <div key={doc.id} className="flex items-center p-2 bg-blue-50 rounded-lg">
                            {getDocumentIcon(doc)}
                            <span className="ml-2 text-sm text-gray-900">{doc.originalName}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-900 mb-1">
                        {t('lawsuit.importantNotice', 'Important Notice')}
                      </h4>
                      <p className="text-sm text-yellow-800">
                        {t('lawsuit.submissionWarning', 'Once you submit, this action cannot be undone. The documents will be sent to the respective parties and your lawsuit will officially begin.')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'submission' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-8 text-center">
                {submitLawsuitMutation.isPending ? (
                  <>
                    <LoadingSpinner size="lg" className="mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {t('lawsuit.submittingLawsuit', 'Submitting Your Lawsuit...')}
                    </h2>
                    <p className="text-gray-600">
                      {t('lawsuit.submissionInProgress', 'Please wait while we process your submission.')}
                    </p>
                  </>
                ) : submitLawsuitMutation.isError ? (
                  <>
                    <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {t('lawsuit.submissionFailed', 'Submission Failed')}
                    </h2>
                    <p className="text-gray-600 mb-4">
                      {t('lawsuit.submissionError', 'There was an error submitting your lawsuit. Please try again.')}
                    </p>
                    <button
                      onClick={() => setStep('confirmation')}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                      {t('common.tryAgain', 'Try Again')}
                    </button>
                  </>
                ) : (
                  <>
                    <CheckIcon className="mx-auto h-12 w-12 text-green-500 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {t('lawsuit.submissionSuccessful', 'Submission Successful!')}
                    </h2>
                    <p className="text-gray-600">
                      {t('lawsuit.submissionCompleted', 'Your lawsuit has been successfully submitted.')}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {step === 'selection' && (
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
              <button
                onClick={() => navigate(`/lawsuit/${id}`)}
                className="px-4 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 min-h-[44px]"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => setStep('confirmation')}
                disabled={selectedDocuments.toAccused.length === 0 && selectedDocuments.toCourt.length === 0}
                className="px-4 py-3 sm:py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
              >
                {t('lawsuit.reviewSubmission', 'Review Submission')}
              </button>
            </div>
          )}

          {step === 'confirmation' && (
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
              <button
                onClick={() => setStep('selection')}
                className="px-4 py-3 sm:py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 min-h-[44px]"
              >
                {t('common.back', 'Back')}
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-3 sm:py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 min-h-[44px]"
              >
                {t('lawsuit.submitLawsuit', 'Submit Lawsuit')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* HTML Document Editor */}
      {editingDocument && (
        <FreeHTMLEditor
          document={editingDocument}
          lawsuitId={id}
          isOpen={!!editingDocument}
          onClose={() => setEditingDocument(null)}
          onSave={handleDocumentSaved}
        />
      )}
    </>
  );
};

export default StartSubmissionPage;